import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import ScrollToTop from './components/ScrollToTop';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';

// Public pages
import Home from './pages/Home';
import Shop from './pages/Shop';
import ProductDetails from './pages/ProductDetails';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import OrderConfirmation from './pages/OrderConfirmation';
import OrderStatus from './pages/OrderStatus';
import CustomRequest from './pages/CustomRequest';
import PrivateLibrary from './pages/PrivateLibrary';
import { About, Privacy, Terms, NotFound } from './pages/StaticPages';

// Admin pages
import AdminLogin from './pages/admin/AdminLogin';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import ProductsManagement from './pages/admin/ProductsManagement';
import ProductEditor from './pages/admin/ProductEditor';
import LibrariesManagement from './pages/admin/LibrariesManagement';
import FeaturedManagement from './pages/admin/FeaturedManagement';
import OrderEditor from './pages/admin/OrderEditor';
import InquiryDetail from './pages/admin/InquiryDetail';
import Invoice from './pages/Invoice';
import OrdersManagement from './pages/admin/OrdersManagement';
import InquiriesManagement from './pages/admin/InquiriesManagement';
import Settings from './pages/admin/Settings';

const PublicShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex flex-col min-h-screen">
    <Navbar />
    <main className="flex-1">{children}</main>
    <Footer />
  </div>
);

const App: React.FC = () => {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            {/* Admin routes (no public Navbar/Footer) */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="products" element={<ProductsManagement />} />
              <Route path="products/new" element={<ProductEditor />} />
              <Route path="products/:productId" element={<ProductEditor />} />
              <Route path="libraries" element={<LibrariesManagement />} />
              <Route path="featured" element={<FeaturedManagement />} />
              <Route path="orders" element={<OrdersManagement />} />
              <Route path="orders/new" element={<OrderEditor />} />
              <Route path="orders/:orderId" element={<OrderEditor />} />
              <Route path="inquiries" element={<InquiriesManagement />} />
              <Route path="inquiries/:inquiryId" element={<InquiryDetail />} />
              <Route path="settings" element={<Settings />} />
            </Route>

            {/* Public routes */}
            {/* Home lives at /home; "/" redirects so there is one
                canonical URL for the landing page. replace keeps "/"
                out of history, so Back doesn't bounce through it. */}
            <Route path="/" element={<Navigate to="/home" replace />} />
            <Route path="/home" element={<PublicShell><Home /></PublicShell>} />
            <Route path="/shop" element={<PublicShell><Shop /></PublicShell>} />
            <Route path="/shop/:slug" element={<PublicShell><ProductDetails /></PublicShell>} />
            <Route path="/cart" element={<PublicShell><Cart /></PublicShell>} />
            <Route path="/checkout" element={<PublicShell><Checkout /></PublicShell>} />
            <Route path="/order/confirmation" element={<PublicShell><OrderConfirmation /></PublicShell>} />
            <Route path="/order/status" element={<PublicShell><OrderStatus /></PublicShell>} />
            <Route path="/custom-request" element={<PublicShell><CustomRequest /></PublicShell>} />
            <Route path="/private/:slug" element={<PublicShell><PrivateLibrary /></PublicShell>} />
            <Route path="/invoice/:token" element={<PublicShell><Invoice /></PublicShell>} />
            <Route path="/about" element={<PublicShell><About /></PublicShell>} />
            <Route path="/privacy" element={<PublicShell><Privacy /></PublicShell>} />
            <Route path="/terms" element={<PublicShell><Terms /></PublicShell>} />
            <Route path="*" element={<PublicShell><NotFound /></PublicShell>} />
          </Routes>
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
};

export default App;
